import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "@/studio/studio.css";

import { useStudioScroll, scrollToId } from "@/studio/lib/useStudioScroll";
import { useTheme } from "@/studio/lib/theme";
import Cursor from "@/studio/components/Cursor";
import StudioLoader from "@/studio/components/StudioLoader";
import SignalLockIntro from "@/studio/components/SignalLockIntro";
import ContactModal from "@/studio/components/ContactModal";
import Atmosphere from "@/studio/components/Atmosphere";
import GravityRipple from "@/studio/components/GravityRipple";
import GravityMode from "@/studio/components/GravityMode";
import Nav from "@/studio/sections/Nav";
import Hero from "@/studio/sections/Hero";
import Transition from "@/studio/sections/Transition";
import WhatWeBuild from "@/studio/sections/WhatWeBuild";
import MoreThanWebsite from "@/studio/sections/MoreThanWebsite";
import SelectedWork from "@/studio/sections/SelectedWork";
import Ecosystem from "@/studio/sections/Ecosystem";
import ProjectArchitect from "@/studio/sections/ProjectArchitect";
import Founder from "@/studio/sections/Founder";
import Philosophy from "@/studio/sections/Philosophy";
import Contact from "@/studio/sections/Contact";

export default function StudioSite() {
  useStudioScroll();
  const { theme } = useTheme();
  const [ready, setReady] = useState(false);
  const [titleReady, setTitleReady] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const navigate = useNavigate();

  // SignalLockIntro is now the DEFAULT studio loader.
  // The previous StudioLoader can still be used via ?intro=classic (or legacy / studio).
  const useClassicIntro = useMemo(() => {
    if (typeof window === "undefined") return false;
    const v = new URLSearchParams(window.location.search).get("intro");
    return v === "classic" || v === "legacy" || v === "studio";
  }, []);

  useEffect(() => {
    document.title =
      "PARABOLUM — Premium Web, App & AI Development from Czechia";
    window.scrollTo(0, 0);
  }, []);

  // elegant, hidden admin access — Ctrl/Cmd + Shift + A
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === "A" || e.key === "a")) {
        e.preventDefault();
        navigate("/admin/login");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  // lock scroll while loader is on screen
  useEffect(() => {
    document.body.style.overflow = titleReady ? "" : "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [titleReady]);

  const openContact = () => setContactOpen(true);

  return (
    <div className="studio-root relative min-h-screen" data-theme={theme}>
      <Atmosphere />
      <div className="studio-grain" aria-hidden="true" />
      <GravityRipple />
      <GravityMode />
      {useClassicIntro ? (
        <StudioLoader onComplete={() => { setReady(true); setTitleReady(true); }} />
      ) : (
        <SignalLockIntro
          onReveal={() => setReady(true)}
          onComplete={() => setTitleReady(true)}
        />
      )}
      <Cursor />
      <Nav onContact={openContact} ready={ready} />
      <main className="relative z-10">
        <Hero onContact={openContact} ready={ready} titleReady={titleReady} />
        <Transition />
        <WhatWeBuild />
        <SelectedWork />
        <MoreThanWebsite />
        <Ecosystem />
        <ProjectArchitect onContact={openContact} />
        <Founder />
        <Philosophy />
        <Contact onContact={openContact} />
      </main>

      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  );
}
